import { createReducer, on } from "@ngrx/store";
import * as RecipeActions from './recipe.actions';
import { Recipe, RecipeDetails } from "../../../shared/models/recipe.model";
import { QuotaInfo } from "../../../shared/models/quota-info.model";

export interface RecipeState {
    recipes: Recipe[];
    selectedDetails: RecipeDetails | null;
    loading: boolean;
    error: any;
    total: number;
    pagination: {
      pageIndex: number;
      pageSize: number;
    };
    searchTerm: string;
    filters: RecipeFilterMap;
    author_name?: string;
    quota: QuotaInfo | null;
    isLikeQuotaLoading: boolean;
  }

  export interface RecipeFilterMap {
    foodType: string[];
    difficulty: string[];
    dietary: string[];
    language: string[];
    affordability: string[];
    leftoverFriendly: boolean | null;
    isSeasonalNow?: boolean;
  }
  
  export const initialState: RecipeState = {
    recipes: [],
    selectedDetails: null,
    loading: false,
    error: null,
    total: 0,
    pagination: {
      pageIndex: 0,
      pageSize: 9
    },
    searchTerm: '',
    filters: {
        foodType: [],
        difficulty: [],
        dietary: [],
        language: [],
        affordability: [],
        leftoverFriendly: null,
        isSeasonalNow: null,
    },
    author_name: null,
    quota: null,
    isLikeQuotaLoading: false,
  };
  
  export const recipeReducer = createReducer(
    initialState,
  
    // Load all
    on(RecipeActions.loadRecipes, state => ({ ...state, loading: true })),
    on(RecipeActions.loadRecipesSuccess, (state, { recipes, total }) => ({
      ...state,
      loading: false,
      recipes,
      total
    })),
    on(RecipeActions.changePage, (state, { pageIndex, pageSize }) => ({
        ...state,
        pagination: { pageIndex, pageSize }
      })),
    on(RecipeActions.loadRecipesFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
    })),
  
    // Load full details
    on(RecipeActions.loadRecipeDetails, state => ({ ...state, loading: true })),
    on(RecipeActions.loadRecipeDetailsSuccess, (state, { details }) => ({
      ...state,
      loading: false,
      selectedDetails: details
    })),
    on(RecipeActions.loadRecipeDetailsFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
    })),
  
    // Create
    on(RecipeActions.createRecipe, state => ({ ...state, loading: true })),
    on(RecipeActions.createRecipeSuccess, (state, { recipe }) => ({
      ...state,
      loading: false,
      recipes: [...state.recipes, recipe],
      selectedDetails: null
    })),
    on(RecipeActions.createRecipeFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
    })),
  
    // Update
    on(RecipeActions.updateRecipe, state => ({ ...state, loading: true })),
    on(RecipeActions.updateRecipeSuccess, (state, { recipe }) => ({
      ...state,
      loading: false,
      recipes: state.recipes.map(r => r.recipe_id === recipe.recipe_id ? recipe : r),
      selectedDetails: null
    })),
    on(RecipeActions.updateRecipeFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
    })),
  
    // Delete
    on(RecipeActions.deleteRecipe, state => ({ ...state, loading: true })),
    on(RecipeActions.deleteRecipeSuccess, (state, { id }) => ({
      ...state,
      loading: false,
      recipes: state.recipes.filter(r => r.recipe_id !== id)
    })),
    on(RecipeActions.deleteRecipeFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
    })),
    on(RecipeActions.setSearchTerm, (state, { searchTerm }) => ({
        ...state,
        searchTerm
    })),
    on(RecipeActions.setDefaultRecipeFilters, (state, { language, dietary }) => ({
        ...state,
        filters: {
          ...state.filters,
          language: [language],
          //dietary: [...dietary],
        }
      })),
    on(RecipeActions.setRecipeFilters, (state, { filters }) => ({
        ...state,
        filters
    })),
      
    on(RecipeActions.clearFilters, state => ({
        ...state,
        searchTerm: '',
        filters: {
          foodType: [],
          difficulty: [],
          dietary: [],
          language: [],
          affordability: [],
          leftoverFriendly: null,
          isSeasonalNow: null
        }
    })),
    on(RecipeActions.clearFiltersToDefault, (state, { defaultLanguage }) => ({
      ...state,
      searchTerm: '',
      filters: {
        foodType: [],
        difficulty: [],
        dietary: [],
        language: [defaultLanguage],
        affordability: [],
        leftoverFriendly: null,
        isSeasonalNow: null
      }
    })),
    on(RecipeActions.loadUserRecipes, state => ({ ...state, loading: true })),
    on(RecipeActions.loadUserRecipesSuccess, (state, { recipes, total }) => ({
        ...state,
        recipes,
        total,
        loading: false,
        error: null
    })),
    on(RecipeActions.loadUserRecipesFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),
    on(RecipeActions.loadPendingRecipes, state => ({ ...state, loading: true })),
    on(RecipeActions.loadPendingRecipesSuccess, (state, { recipes, total }) => ({
        ...state,
        recipes,
        total,
        loading: false,
        error: null
    })),
    on(RecipeActions.loadPendingRecipesFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),
    on(RecipeActions.loadSavedRecipes, state => ({ ...state, loading: true })),
    on(RecipeActions.loadSavedRecipesSuccess, (state, { recipes, total }) => ({
        ...state,
        recipes,
        total,
        loading: false,
        error: null
    })),
    on(RecipeActions.loadSavedRecipesFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),

    // Other User's Recipes
    on(RecipeActions.loadOtherUserRecipes, state => ({ ...state, loading: true })),
    on(RecipeActions.loadOtherUserRecipesSuccess, (state, { recipes, total, author_name }) => ({
        ...state,
        recipes,
        total,
        author_name,
        loading: false,
        error: null,
    })),
    on(RecipeActions.loadOtherUserRecipesFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
    })),
    on(RecipeActions.saveRecipeSuccess, (state, { recipeId }) => ({
        ...state,
        recipes: state.recipes.map(r =>
          r.recipe_id === recipeId ? { ...r, is_saved: true } : r
        ),
        selectedDetails: state.selectedDetails?.recipe.recipe_id === recipeId
          ? {
              ...state.selectedDetails,
          recipe: {
            ...state.selectedDetails.recipe,
            is_saved: true
          }
          }
        : state.selectedDetails
      })),

    on(RecipeActions.unsaveRecipeSuccess, (state, { recipeId }) => ({
      ...state,
      recipes: state.recipes.map(r =>
        r.recipe_id === recipeId ? { ...r, is_saved: false } : r
      ),
      selectedDetails: state.selectedDetails?.recipe.recipe_id === recipeId
      ? {
        ...state.selectedDetails,
        recipe: {
          ...state.selectedDetails.recipe,
          is_saved: false
        }
      }
        : state.selectedDetails
      })),
      on(RecipeActions.updateRecipeApproval, state => ({ ...state, loading: true })),
      on(RecipeActions.updateRecipeApprovalSuccess, (state, { recipe }) => ({
        ...state,
        loading: false,
        recipes: state.recipes.map(r =>
          r.recipe_id === recipe.recipe_id ? recipe : r
        ),
        selectedDetails: null
      })),

      on(RecipeActions.updateRecipeApprovalFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error
      })),

    on(RecipeActions.clearSelectedRecipeDetails, state => ({
        ...state,
        selectedDetails: null,
        loading: false,
    })),
    on(RecipeActions.clearAllRecipes, (state) => ({
        ...state,
        recipes: [],
        total: 0,
        pagination: {
          pageIndex: 0,
          pageSize: 9
        },
        searchTerm: '',
        filters: {
          foodType: [],
          difficulty: [],
          dietary: [],
          language: [],
          affordability: [],
          leftoverFriendly: null,
          isSeasonalNow: null
        },
        author_name: null,
        quota: null,
        isLikeQuotaLoading: false,
    })),
    on(RecipeActions.loadLikeQuota, state => ({ ...state, isLikeQuotaLoading: true })),
    on(RecipeActions.loadLikeQuotaSuccess, (state, { quota }) => ({
          ...state,
          quota,
          isLikeQuotaLoading: false
    })),
    on(RecipeActions.loadLikeQuotaFailure, (state) => ({
          ...state,
          quota: null,
          isLikeQuotaLoading: false
    })),
);